import React, { HTMLAttributes, createElement } from 'react';

import { useIntl } from 'react-intl';
import clsx from 'clsx';
import css from 'styled-jsx/css';
import truncate from 'truncate';

import { messageToString, Message as MessageType } from '@utils/message';

import { colors, screens } from '@styles';

import { types, TextType } from './text.types';
import { TextProps, TextStyle } from './text.props';
import { elements } from './text.elements';

import style from './text.module.scss';

const { h1, h2, h3, h4, h5, h6, hero1, hero2, hero3 } = types;
const headings: TextType[] = [h1, h2, h3, h4, h5, h6];
const heros: TextType[] = [hero1, hero2, hero3];

type Props = TextProps;

const applyStyle = ({
  color,
  textAlign,
  margin,
  fontWeight,
  lineHeight,
  maxWidth,
  fontSize,
  cursor,
  decoration,
}: TextStyle) => {
  const result: string[] = [];

  if (color) {
    if (color === 'inherit') {
      result.push('color: inherit;');
    } else {
      result.push(`color: ${colors[color].hex()};`);
    }
  }
  if (textAlign) result.push(`text-align: ${textAlign};`);
  if (cursor) result.push(`cursor: ${cursor};`);
  if (decoration) result.push(`text-decoration: ${decoration};`);
  if (typeof margin !== 'undefined') {
    if (typeof margin === 'string') {
      result.push(`margin: ${margin}`);
    } else if (Array.isArray(margin)) {
      // Make sure all number values have px
      const convertedMargin = margin.map((value) =>
        typeof value === 'number' ? `${value}px` : value,
      );

      result.push(`margin: ${convertedMargin.join(' ')};`);
    } else {
      result.push(`margin: ${margin}px;`);
    }
  }
  if (typeof fontWeight !== 'undefined')
    result.push(`font-weight: ${fontWeight};`);
  if (typeof lineHeight !== 'undefined')
    result.push(`line-height: ${lineHeight};`);
  if (typeof maxWidth !== 'undefined') {
    if (typeof maxWidth === 'string') {
      result.push(`max-width: ${maxWidth};`);
    } else {
      result.push(`max-width: ${maxWidth}px;`);
    }
  }

  if (typeof fontSize === 'string') result.push(`font-size: ${fontSize};`);
  if (typeof fontSize === 'number') result.push(`font-size: ${fontSize}px;`);

  return result.join('\n');
};

type StyleParams = {
  sm?: TextStyle;
  md?: TextStyle;
  lg?: TextStyle;
  xl?: TextStyle;
} & TextStyle;

const getStyles = ({ sm, md, lg, xl, ...textStyle }: StyleParams) => {
  return css.resolve`
    .dynamic {
      ${applyStyle(textStyle)}
    }

    @media (min-width: ${screens.sm}px) {
      .dynamic {
        ${sm ? applyStyle(sm) : ''}
      }
    }

    @media (min-width: ${screens.md}px) {
      .dynamic {
        ${md ? applyStyle(md) : ''}
      }
    }

    @media (min-width: ${screens.lg}px) {
      .dynamic {
        ${lg ? applyStyle(lg) : ''}
      }
    }

    @media (min-width: ${screens.xl}px) {
      .dynamic {
        ${xl ? applyStyle(xl) : ''}
      }
    }
  `;
};

export const Text = ({
  type = types.regular,
  element = elements.p,
  sm,
  md,
  lg,
  xl,
  className,
  onClick,
  message,
  children,
  innerRef,
  fontFamily,
  truncate: truncateLength,
  ...textStyle
}: Props) => {
  const intl = useIntl();

  const isHero = heros.includes(type);
  const isHeading = headings.includes(type);

  const { className: dynamicClassName, styles } = getStyles({
    sm,
    md,
    lg,
    xl,
    ...textStyle,
  });

  const props: HTMLAttributes<HTMLDivElement> = {
    className: clsx(
      style.text,
      style[type],
      isHero && style.hero,
      isHeading && style.heading,
      'dynamic',
      dynamicClassName,
      fontFamily && style[fontFamily],
      className,
    ),
    onClick,
    children,
  };

  if (message) {
    // content
    let content = messageToString(message, intl);

    if (truncateLength && typeof content === 'string') {
      content = truncate(content, truncateLength);
    }

    if (message.disableHTMLRender) {
      props.children = content;
    } else if (content && typeof content === 'string') {
      props.dangerouslySetInnerHTML = { __html: content };
    }
  }

  // element
  const elementComponent = createElement(element, {
    ...props,
    ref: innerRef,
  });

  return (
    <>
      {elementComponent}
      {styles}
    </>
  );
};

export const textTypes = types;
export type Message = MessageType;
